import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, take, takeUntil } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { SnCurrentOrganization } from '@lib/auth/services';
import { FsStateConvertPipe } from '@lib/common/pipes/state-convert.pipe';

import { ViLPRProvider } from '../../shared/models';
import { getBoundingBox } from '../../utils/utils';
import { LPR_ERRORS, LprWarningEntry } from '../constants/errors';
import {
  PlateResultSort,
  VehicleBodyTypes,
  VehicleColorTypes,
  VehicleMakeTypes,
} from '../constants/parameters';
import { IFsHistoricalProviderResultsInterface, IFsLprReadsParameters } from '../interfaces';
import { IFsLprParameters } from '../interfaces/common/parameters';
import { IFsLprProviderInterface } from '../interfaces/common/provider';
import { IFsLprResult } from '../interfaces/common/result';
import { LprReadsFieldsModel } from '../models';
import { LprReadsModel } from '../models/lpr-reads.model';
import { FsLprReadsService } from '../services/lpr-reads.service';

/**
 * Fusus provider service
 * @description This service is used to search for license plate numbers using the LPR Ingest API
 * @url API: https://lpr-api.dev.fususone.com/docs#/reads/get_reads_api_reads__get
 * @url BE Codebase: https://bitbucket.org/securonet/lpr-ingest/src/master/
 */
@Injectable({ providedIn: 'root' })
export class FususProviderService implements IFsLprProviderInterface {
  public name = 'Fusus';
  public nameEnum?: ViLPRProvider;
  private readonly _MAX_SIZE: number = 100; // As per API docs linked above, limit is 100

  public get loaded$(): Observable<boolean> {
    return this._loaded$;
  }

  public get historicLoaded$(): Observable<boolean> {
    return this._historicLoaded$;
  }

  public errors: string[] = [];
  public warnings: LprWarningEntry[] = [];

  public isLoading: boolean;
  public isInit: boolean;
  public isFinished: boolean = false;
  public fields: LprReadsFieldsModel;
  private _validColorsMap: Map<string, string[]> = new Map();

  private _params: IFsLprReadsParameters;
  private _loaded$: Subject<boolean> = new Subject<boolean>();
  private _inited$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _historicLoaded$: Subject<boolean> = new Subject<boolean>();

  private readonly _destroy$: Subject<void> = new Subject<void>();
  private readonly _cancel$: Subject<void> = new Subject<void>();
  private readonly _plateStateConvertPipe: FsStateConvertPipe = new FsStateConvertPipe();

  constructor(
    public readonly _lprReadsService: FsLprReadsService,
    private readonly _currentOrganization: SnCurrentOrganization,
  ) {
    if (this._sdmEnabled) {
      this.nameEnum = ViLPRProvider.FUSUS;
      this.isLoading = true;
      this.isInit = false;
      this.total = 0;
    }
  }

  public results: IFsLprResult[] = [];
  public total: number = 0;

  public init(): void {
    this._lprReadsService
      .getReadsFields()
      .pipe(
        catchError(() => {
          this.errors.push(LPR_ERRORS.FAILED_TO_LOAD_FIELDS);
          this.isFinished = true;
          this.total = 0;
          return of(null);
        }),
      )
      .subscribe((res: LprReadsFieldsModel) => {
        if (res) {
          this.fields = res;
          this._buildValidColorsMap();
          this._inited$.next(true);
          this.isInit = true;
        }
      });
  }

  /**
   * Build a map of lowercase color -> all valid variations, as vehicleColor isn't currently normalized. (Ex: WHITE, White, BLUE, Blue)
   */
  private _buildValidColorsMap(): void {
    this._validColorsMap.clear();
    this.fields?.vehicleColor?.forEach((color) => {
      const lowerColorKey = color.name.toLowerCase();
      const validColorValues = this._validColorsMap.get(lowerColorKey);
      if (validColorValues) {
        validColorValues.push(color.name);
      } else {
        this._validColorsMap.set(lowerColorKey, [color.name]);
      }
    });
  }

  public search(query: Partial<IFsLprParameters>): void {
    const inited$ = this._sdmEnabled
      ? this._inited$.pipe(
          filter((inited) => inited),
          take(1),
        )
      : this._inited$;
    inited$.subscribe((inited) => {
      if (inited) {
        this._startSearch(query);
      }
    });
  }

  public next(): void {
    if (this.isFinished || this.isLoading) {
      return;
    }
    this._params.page++;
    this.isLoading = true;
    if (this._sdmEnabled) {
      this._loaded$.next(false);
    }
    this._lprReadsService
      .getReads(this._params)
      .pipe(
        takeUntil(this._cancel$),
        catchError((err) => {
          this.errors.push(LPR_ERRORS.FAILED_TO_LOAD_NEXT_PAGE);
          this.isLoading = false;
          this.isFinished = true;
          this._loaded$.next(true);
          return of(null);
        }),
      )
      .subscribe(this._handleResponse.bind(this));
  }

  public getHistoricalResults(
    searchParams: Partial<IFsLprParameters>,
  ): Observable<IFsHistoricalProviderResultsInterface> {
    const modifiedParams: IFsLprReadsParameters =
      this._historicalSearchTransformParams(searchParams);

    return this._lprReadsService.getReads(modifiedParams).pipe(
      map((response: LprReadsModel) => {
        return {
          results: response.items || [],
          total: response.total || 0,
        };
      }),
      catchError(() => {
        return of(null);
      }),
      takeUntil(this._destroy$),
    );
  }

  public cancel(): void {
    this._cancel$.next();
  }

  private get _sdmEnabled(): boolean {
    return this._currentOrganization?.settings?.sourceAndDeviceManagementEnabled ?? false;
  }

  private _startSearch(query: Partial<IFsLprParameters>): void {
    this.cancel(); // Cancel any ongoing search
    this.isFinished = false;
    this.errors = [];
    this.warnings = [];
    this.results = [];
    this.isLoading = true;
    if (this._sdmEnabled) {
      this._loaded$.next(false);
    }
    this.total = 0;
    const params: IFsLprReadsParameters = this._searchTransformParams(query);
    if (this.errors.length > 0) {
      this.isLoading = false;
      this.isFinished = true;
      this._loaded$.next(true);
      return;
    }
    this._lprReadsService
      .getReads(params)
      .pipe(
        takeUntil(this._cancel$),
        catchError((err) => {
          this.errors.push(LPR_ERRORS.FAILED_TO_LOAD_SEARCH_RESULTS);
          this.isLoading = false;
          this.isFinished = true;
          this.total = 0;
          this._loaded$.next(true);
          return of(null);
        }),
      )
      .subscribe(this._handleResponse.bind(this));
  }

  private _handleResponse(response: LprReadsModel): void {
    if (response) {
      const items = response.items || [];
      this.total = response.total || 0;
      this.results = [...this.results, ...items];
      this.isLoading = false;
      this.isFinished = this.results.length >= this.total || items.length === 0;
      this._loaded$.next(true);
    }
  }

  private _baseTransformParams(params: Partial<IFsLprParameters>): IFsLprReadsParameters {
    const plate = params.plate == '' ? null : params.plate;

    const transformedParams: IFsLprReadsParameters = {
      page: 1,
      search_reason: params.searchReason,
      plate,
      plate_state: this._plateStateConvertPipe.transform(params.plateState, 'short'),
      event_timestamp_from: params.fromDate?.toISOString(),
      event_timestamp_to: params.toDate?.toISOString(),
      ordering: params.sort === PlateResultSort.ASC ? 'event_timestamp' : '-event_timestamp',
      ...(params.limit ? { size: Math.min(params.limit, this._MAX_SIZE) } : {}),
      ...(params.inBbox
        ? {
            polygon: getBoundingBox(
              params.inBbox,
              this._sdmEnabled && this.nameEnum ? this.nameEnum : this.name,
            ),
          }
        : {}),

      // we only want to filter by `is_hit` if we are explicitly looking for the `hit` recordType
      // otherwise, we will return all hits and reads (default behavior)
      ...(params.recordType === 'hit' ? { is_hit: true } : {}),
    };

    return transformedParams;
  }

  private _searchTransformParams(params: Partial<IFsLprParameters>): IFsLprReadsParameters {
    this._params = this._baseTransformParams(params);

    if (params.vehicleColor) {
      this._setColor(
        Array.isArray(params.vehicleColor) ? params.vehicleColor : [params.vehicleColor],
      );
    }

    if (params.vehicleMake) {
      this._setVehicleMake(params.vehicleMake);
    }

    if (params.vehicleBody) {
      this._setVehicleBody(params.vehicleBody);
    }

    if (params.assignedUserIds?.length) {
      this.warnings.push({
        type: 'ASSIGNED_USER_IDS_NOT_SUPPORTED',
        message: LPR_ERRORS.ASSIGNED_USER_IDS_NOT_SUPPORTED,
      });
    }

    if (params.deviceNames?.length) {
      this.warnings.push({
        type: 'DEVICE_NAMES_NOT_SUPPORTED',
        message: LPR_ERRORS.DEVICE_NAMES_NOT_SUPPORTED,
      });
    }

    return this._params;
  }

  private _historicalSearchTransformParams(
    params: Partial<IFsLprParameters>,
  ): IFsLprReadsParameters {
    return this._baseTransformParams(params);
  }

  private _setColor(colors: string[]): void {
    // Reduce the input colors into supported and notSupported sets.
    const { supported, notSupported } = colors.reduce(
      (acc, color) => {
        const lowerColor = color.toLowerCase();

        // Manually map silver_grey to what's returned by getReadsFields/vehicleColor
        // TODO AXFX-1387: Remove this once it's normalized!
        // https://taserintl.atlassian.net/browse/AXFS-1387
        if (lowerColor === VehicleColorTypes.SILVER_GREY && !this._validColorsMap.has(lowerColor)) {
          // Currently, lpr-api.stg is returning 'Silver/Gray', while dev is returning 'SILVER' and 'GRAY' separately. The 'grey' spelling variations are there just in case.
          const candidateKeys = ['silver/gray', 'silver/grey', 'silver', 'gray', 'grey'];
          const initialSize = acc.supported.size;

          candidateKeys.forEach((key) => {
            const validValues = this._validColorsMap.get(key);
            if (validValues) {
              validValues.forEach((value) => acc.supported.add(value));
            }
          });

          // If nothing was added, treat it as not supported.
          if (acc.supported.size === initialSize) {
            acc.notSupported.add(color);
          }
        } else {
          const validColorValues = this._validColorsMap.get(lowerColor);
          if (validColorValues) {
            validColorValues.forEach((value) => acc.supported.add(value));
          } else {
            acc.notSupported.add(color);
          }
        }
        return acc;
      },
      { supported: new Set<string>(), notSupported: new Set<string>() },
    );

    if (notSupported.size) {
      const nonSupportedColorsString = Array.from(notSupported).join(', ');
      this.warnings.push({
        type: 'INVALID_VEHICLE_COLORS',
        message: LPR_ERRORS.INVALID_VEHICLE_COLORS(nonSupportedColorsString),
      });
    }

    if (supported.size) {
      this._params.vehicle_color = Array.from(supported);
    }
  }

  private _setVehicleMake(make: VehicleMakeTypes): void {
    const makeField = this.fields.vehicleMake.find(
      (field) => field.name.toLowerCase() === make.toLowerCase(),
    );
    if (makeField) {
      this._params.vehicle_make = [makeField.name];
    } else {
      this.warnings.push({
        type: 'INVALID_VEHICLE_MAKE',
        message: LPR_ERRORS.INVALID_VEHICLE_MAKE(make),
      });
    }
  }

  private _setVehicleBody(body: VehicleBodyTypes): void {
    const bodyField = this.fields.vehicleBodyType.find(
      (field) => field.name.toLowerCase() === body.toLowerCase(),
    );
    if (bodyField) {
      this._params.vehicle_body_type = [bodyField.name];
    } else {
      this.warnings.push({
        type: 'INVALID_VEHICLE_TYPE',
        message: LPR_ERRORS.INVALID_VEHICLE_TYPE(body),
      });
    }
  }
}
